'use client'

import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useMutation, useQuery, useLazyQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'
import { ImageUploadV2 } from '@/components/shared/inputComponents/ImageUploadV2'
import { VideoUploadV2 } from '@/components/shared/inputComponents/VideoUploadV2'
import { RichTextField } from '@/components/shared/inputComponents/RichTextField'
import {
  CREATE_CONTENT_BLOCK,
  CREATE_BUTTON_CONTENT_BLOCK,
  CREATE_LINKED_CONTENT_BLOCK,
  GET_CONTENT_BLOCK,
  UPDATE_CONTENT_BLOCK,
  UPDATE_BUTTON_CONTENT_BLOCK,
  UPDATE_LINKED_CONTENT_BLOCK,
  DELETE_MANY_CONTENT_BLOCKS,
} from '../contentBlockQueries'
import { Icons } from '@/components/shared/icons'
import { useConfirmation } from '@/context/ConfirmationContext'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { ContentBlockFormValues } from '../types'
import { StyleSelector } from './StyleSelector'
import { StyleEditor } from './StyleEditor'
import { TitleAlignmentControl } from './TitleAlignmentControl'
import ContentBlockUsages from './ContentBlockUsages'
import { GET_CATEGORIES_FOR_SELECT } from '../../Categories/categoriesQueries'
import { Plus, X, GripVertical } from 'lucide-react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import GridProductPicker from './GridProductPicker'
import TagsSelect from '@/components/shared/inputComponents/TagsSelect'
import CtaEditor from '@/components/shared/CtaEditor'
import { naturalTitleAlign } from '../contentBlockPayload'
import {
  GET_PAGES_FOR_SELECTOR,
  PageSelector,
  imageTypes,
  videoTypes,
  videoTypeOptions,
  textStyles,
  galleryStyles,
  textAndImageStyles,
  buttonStyles,
  contentBlockTypes,
} from '../contentBlockConstants'

const formSchema = z.object({
  content_block_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        description_format: z.enum(['plain', 'markdown']).optional().default('plain'),
        link: z.string().optional().default(''),
        cta_label: z.string().optional().default(''),
        cta_link: z.string().optional().default(''),
        language: z.string(),
      })
    )
    .transform(items =>
      items.filter(
        item =>
          item.name?.trim() ||
          item.cta_label?.trim() ||
          item.cta_link?.trim() ||
          item.description?.trim()
      )
    ),
  content_block_type: z.enum([
    'only_text',
    'only_image',
    'text_and_image',
    'only_video',
    'text_and_video',
    'gallery',
    'button',
    'photo_links',
    'category_tiles',
    'product_grid',
    'page_grid',
    'email_signup',
    'pdf_flipbook',
  ]),
  style: z
    .enum([
      '1_column_title_top',
      '2_columns_title_top_center',
      '2_columns_1_3',
      '3_columns_title_left',
      'masonry',
      'spread_harmony',
      'scroll_rhythm',
      'gallery_side_by_side',
      'side_by_side',
      'text_on_image',
      'image_left',
      'outline',
      'filled',
    ])
    .optional(),
  images: z.array(z.string()),
  gallery_images: z.array(z.string()),
  grid_product_picks: z
    .array(z.object({ id: z.number() }))
    .optional()
    .default([]),
  video_link: z.string().optional(),
  video_type: z.enum(['youtube', 'vimeo', 'uploaded']).optional(),
  video_autoplay: z.boolean(),
  video_loop: z.boolean(),
  video_controls: z.boolean(),
  default_margins: z.boolean(),
  object_fit_cover: z.boolean(),
  extra_css: z
    .string()
    .optional()
    .refine(
      val => {
        if (!val || val.trim() === '') return true
        try {
          JSON.parse(val)
          return true
        } catch {
          return false
        }
      },
      { message: 'Invalid JSON format' }
    ),
  text_color: z.string().optional(),
  background_color: z.string().optional(),
  link_new_tab: z.boolean(),
  link_page_id: z.string(),
  cta_new_tab: z.boolean(),
  cta_link_page_id: z.string(),
  cta_link_type: z.enum(['cms_page', 'category', 'store']).nullable().optional(),
  cta_link_category_id: z.number().nullable().optional(),
  linked_items: z.array(
    z.object({
      id: z.number().optional(),
      title: z.string(),
      link: z.string(),
      arrangement: z.number(),
      image: z.string(),
      imageId: z.number().nullable().optional(),
    })
  ),
  selected_category_ids: z.array(z.number()),
  product_grid_mode: z.enum(['newest', 'manual']).optional().default('newest'),
  product_grid_max_products: z.number().min(1).max(8).optional().default(8),
  product_grid_tag_ids: z.array(z.number()).optional().default([]),
  page_grid_mode: z.enum(['manual', 'children']).optional().default('manual'),
  page_grid_max_pages: z.number().min(1).max(24).optional().default(8),
  page_grid_items_per_row: z.number().min(2).max(6).optional().default(4),
  selected_page_ids: z.array(z.string()),
  page_grid_parent_id: z.string().optional(),
  page_grid_show_tags: z.boolean().optional().default(false),
  email_signup_delivery: z
    .enum(['email', 'mailerlite'])
    .optional()
    .default('email'),
  email_signup_notify_email: z
    .string()
    .optional()
    .refine(
      val => {
        if (!val || val.trim() === '') return true
        return z.string().email().safeParse(val).success
      },
      { message: 'Invalid email address' }
    ),
  titleAlignment: z.enum(['left', 'center', 'right']).optional(),
  hideTitle: z.boolean().optional().default(false),
  textOnImageDisplay: z
    .enum(['full_width', 'content_width', 'text_width'])
    .optional(),
  mediaRatio: z.enum(['50_50', '60_40', '40_60']).optional(),
})
  .superRefine((data, ctx) => {
    // Data-driven blocks (grids / linked items) configure themselves from the
    // selected pages/products/categories, so a display name is optional. Every
    // other type still requires at least one filled language profile.
    const nameOptionalTypes = [
      'photo_links',
      'category_tiles',
      'product_grid',
      'page_grid',
    ]
    if (
      !nameOptionalTypes.includes(data.content_block_type) &&
      data.content_block_profiles.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one language profile needs to be filled',
        path: ['content_block_profiles', 0, 'name'],
      })
    }
  }) satisfies z.ZodType<ContentBlockFormValues>

interface ContentBlockFormProps {
  data: {
    id: number | null
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function ContentBlockForm({
  data,
  onDirtyChange,
  onRequestClose,
}: ContentBlockFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [id, setId] = useState(data?.id)

  // Bumped every time an update mutation completes, so the "Used on"
  // (ContentBlockUsages) panel below the Update button refetches and shows
  // what was just saved instead of the snapshot from when the form opened.
  const [usagesRefreshKey, setUsagesRefreshKey] = useState(0)

  // The FULL `config` object as loaded (edit) or `{}` (create). Retained so the
  // save can spread it FIRST when rebuilding config — the custom mutations null
  // config when it isn't sent, so keys the form doesn't rebuild (title settings,
  // grid keys, category_ids, …) must be carried through explicitly.
  const rawConfigRef = useRef<Record<string, unknown>>({})

  const {
    data: { findUniqueContentBlock: contentBlockData } = {},
    loading: loadingData,
  } = useQuery(GET_CONTENT_BLOCK, {
    variables: {
      id: id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const form = useForm<ContentBlockFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      content_block_profiles: [],
      content_block_type: 'only_text',
      style: undefined,
      images: [],
      gallery_images: [],
      grid_product_picks: [],
      video_link: '',
      video_type: undefined,
      video_autoplay: false,
      video_loop: false,
      video_controls: true,
      default_margins: true,
      object_fit_cover: false,
      extra_css: '',
      text_color: '',
      background_color: '',
      link_new_tab: false,
      link_page_id: '',
      cta_new_tab: false,
      cta_link_page_id: '',
      cta_link_type: null,
      cta_link_category_id: null,
      linked_items: [],
      selected_category_ids: [],
      product_grid_mode: 'newest',
      product_grid_max_products: 8,
      product_grid_tag_ids: [],
      page_grid_mode: 'manual',
      page_grid_max_pages: 8,
      page_grid_items_per_row: 4,
      selected_page_ids: [],
      page_grid_parent_id: '',
      page_grid_show_tags: false,
      mediaRatio: '50_50',
      email_signup_delivery: 'email',
      email_signup_notify_email: '',
    },
  })

  const selectedType = form.watch('content_block_type')
  const showImageUpload = imageTypes.includes(selectedType)
  const showVideoInput = videoTypes.includes(selectedType)
  const selectedVideoType = form.watch('video_type')
  const isGallery = selectedType === 'gallery'
  const isTextAndImage = selectedType === 'text_and_image'
  const isButton = selectedType === 'button'
  const showStyleDropdown =
    selectedType === 'only_text' || isGallery || isTextAndImage || isButton
  const showLinkConfig = isButton
  const showDescription = !isButton
  const isPhotoLinks = selectedType === 'photo_links'
  const isCategoryTiles = selectedType === 'category_tiles'
  const isProductGrid = selectedType === 'product_grid'
  const productGridMode = form.watch('product_grid_mode')
  const isPageGrid = selectedType === 'page_grid'
  const pageGridMode = form.watch('page_grid_mode')
  const isEmailSignup = selectedType === 'email_signup'
  const emailSignupDelivery = form.watch('email_signup_delivery')
  const showRecipientEmail = (emailSignupDelivery || 'email') === 'email'
  const selectedStyle = form.watch('style')

  const [loadCategories, { data: categoriesData, loading: loadingCategories }] =
    useLazyQuery(GET_CATEGORIES_FOR_SELECT, {
      fetchPolicy: 'cache-and-network',
    })

  useEffect(() => {
    if (isCategoryTiles) {
      loadCategories()
    }
  }, [isCategoryTiles, loadCategories])

  const allCategories = categoriesData?.findManyCategory || []
  const currentLang = allLanguages[languageId] || allLanguages[0]

  const getCategoryName = (profiles: { name: string; language: string }[]) => {
    return (
      profiles.find(p => p.language === currentLang)?.name ||
      profiles[0]?.name ||
      'Unknown'
    )
  }

  const selectedCategoryIds = form.watch('selected_category_ids') || []
  const selectedCategoryIdSet = new Set(selectedCategoryIds)
  const unassignedCategories = allCategories.filter(
    (c: { id: number }) => !selectedCategoryIdSet.has(c.id)
  )

  const handleAddCategory = (categoryId: number) => {
    const current = form.getValues('selected_category_ids') || []
    form.setValue('selected_category_ids', [...current, categoryId])
  }

  const handleRemoveCategory = (categoryId: number) => {
    const current = form.getValues('selected_category_ids') || []
    form.setValue(
      'selected_category_ids',
      current.filter(id => id !== categoryId)
    )
  }

  const handleCategoryDragEnd = (result: any) => {
    if (!result.destination) return
    const current = [...(form.getValues('selected_category_ids') || [])]
    const [moved] = current.splice(result.source.index, 1)
    current.splice(result.destination.index, 0, moved)
    form.setValue('selected_category_ids', current)
  }

  const { data: pagesData, loading: loadingPages } = useQuery(
    GET_PAGES_FOR_SELECTOR
  )
  const allPages = pagesData?.findManyPage || []

  const getPageTitle = (page: {
    code: string
    page_profiles: { title: string; language: string }[]
  }) => {
    return (
      page.page_profiles?.find(p => p.language === currentLang)?.title ||
      page.code
    )
  }

  const selectedPageIds = form.watch('selected_page_ids') || []
  const selectedPageIdSet = new Set(selectedPageIds)
  const unassignedPages = allPages.filter(
    (p: { id: string }) => !selectedPageIdSet.has(p.id)
  )

  const handleAddPage = (pageId: string) => {
    const current = form.getValues('selected_page_ids') || []
    form.setValue('selected_page_ids', [...current, pageId])
  }

  const handleRemovePage = (pageId: string) => {
    const current = form.getValues('selected_page_ids') || []
    form.setValue(
      'selected_page_ids',
      current.filter(id => id !== pageId)
    )
  }

  const handlePageDragEnd = (result: any) => {
    if (!result.destination) return
    const current = [...(form.getValues('selected_page_ids') || [])]
    const [moved] = current.splice(result.source.index, 1)
    current.splice(result.destination.index, 0, moved)
    form.setValue('selected_page_ids', current)
  }

  const [updateContentBlock] = useMutation(UPDATE_CONTENT_BLOCK, {
    onError: () => {
      toast.error('Failed to update content block')
    },
    onCompleted: () => {
      toast.success('Content block updated successfully')
      setUsagesRefreshKey(k => k + 1)
    },
    refetchQueries: ['GET_CONTENT_BLOCKS', 'GetContentBlock'],
  })

  const [updateButtonContentBlock] = useMutation(UPDATE_BUTTON_CONTENT_BLOCK, {
    onError: () => {
      toast.error('Failed to update content block')
    },
    onCompleted: () => {
      toast.success('Content block updated successfully')
      setUsagesRefreshKey(k => k + 1)
    },
    refetchQueries: ['GET_CONTENT_BLOCKS', 'GetContentBlock'],
  })

  const [createContentBlock] = useMutation(CREATE_CONTENT_BLOCK, {
    onError: () => {
      toast.error('Failed to create content block')
    },
    onCompleted: response => {
      toast.success('Content block created successfully')
      const newId = response.createNewContentBlock.id
      setId(newId)
    },
    refetchQueries: ['GET_CONTENT_BLOCKS'],
  })

  const [createButtonContentBlock] = useMutation(CREATE_BUTTON_CONTENT_BLOCK, {
    onError: () => {
      toast.error('Failed to create content block')
    },
    onCompleted: response => {
      toast.success('Content block created successfully')
      const newId = response.createNewContentBlock.id
      setId(newId)
    },
    refetchQueries: ['GET_CONTENT_BLOCKS'],
  })

  const [createLinkedContentBlock] = useMutation(CREATE_LINKED_CONTENT_BLOCK, {
    onError: () => {
      toast.error('Failed to create content block')
    },
    onCompleted: response => {
      toast.success('Content block created successfully')
      const newId = response.createNewContentBlock.id
      setId(newId)
    },
    refetchQueries: ['GET_CONTENT_BLOCKS'],
  })

  const [updateLinkedContentBlock] = useMutation(UPDATE_LINKED_CONTENT_BLOCK, {
    onError: () => {
      toast.error('Failed to update content block')
    },
    onCompleted: () => {
      toast.success('Content block updated successfully')
      setUsagesRefreshKey(k => k + 1)
    },
    refetchQueries: ['GET_CONTENT_BLOCKS', 'GetContentBlock'],
  })

  const [deleteContentBlocks] = useMutation(DELETE_MANY_CONTENT_BLOCKS, {
    refetchQueries: ['GET_CONTENT_BLOCKS'],
  })

  const { showConfirmation } = useConfirmation()

  const handleDelete = () => {
    if (id) {
      showConfirmation({
        onConfirm: async () => {
          try {
            await deleteContentBlocks({
              variables: { ids: [id] },
            })
            toast.success('Content block deleted successfully')
            onRequestClose?.()
          } catch (error) {
            toast.error('Failed to delete content block')
          }
        },
        description: 'Are you sure you want to delete this content block?',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
    }
  }

  function onSubmit(values: ContentBlockFormValues) {
    const variables: Record<string, unknown> = {
      content_block_profiles: values.content_block_profiles.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description,
        description_format: p.description_format ?? 'plain',
        link: p.link || null,
        cta_label: p.cta_label || null,
        cta_link: p.cta_link || null,
        language: p.language,
      })),
      content_block_type: values.content_block_type,
      style: values.style || null,
      images: values.images,
      gallery_images: values.gallery_images,
      // `selected_products` (PDP attachment) is DELIBERATELY never sent from
      // this form — it's managed on the product side via
      // `updateProductContainerContentBlocks`. Omitting the key means the
      // (now-optional) mutation variable is undefined and Apollo omits it
      // from the request, which the resolver reads as "leave untouched".
      video_link: values.video_link || null,
      video_type: values.video_type || null,
      video_autoplay: values.video_autoplay,
      video_loop: values.video_loop,
      default_margins: values.default_margins,
      object_fit_cover: values.object_fit_cover,
      extra_css: values.extra_css ? JSON.parse(values.extra_css) : null,
      text_color: values.text_color || null,
      background_color: values.background_color || null,
      cta: {
        new_tab: values.cta_new_tab,
        link_page_id: values.cta_link_page_id || null,
        // Legacy rows saved before `cta_link_type` existed only have
        // `cta_link_page_id` set — fall back to `cms_page` so an untouched
        // save of a pre-existing CMS-page CTA doesn't send `link_type: null`
        // (which would disconnect the page FK). Mirrors the portal's own
        // `resolveCtaHref` legacy fallback.
        link_type:
          values.cta_link_type ?? (values.cta_link_page_id ? 'cms_page' : null),
        link_category_id: values.cta_link_category_id ?? null,
      },
    }

    const isButtonType = values.content_block_type === 'button'

    if (isButtonType) {
      variables.link_new_tab = values.link_new_tab
      variables.link_page_id = values.link_page_id || null
    }

    const isLinkedType =
      values.content_block_type === 'photo_links' ||
      values.content_block_type === 'category_tiles' ||
      values.content_block_type === 'product_grid' ||
      values.content_block_type === 'page_grid'

    if (isLinkedType) {
      variables.linked_items =
        values.linked_items?.map((item, i) => ({
          ...(item.id && { id: item.id }),
          title: item.title,
          link: item.link,
          arrangement: i,
          image: item.image || null,
          imageId: item.imageId || null,
        })) ?? []
      // Always emit `category_ids` (even when empty) so this form-managed key
      // wins over the spread-first loaded config in the merge below — otherwise
      // clearing all categories could not persist (the old ids would survive).
      variables.config = { category_ids: values.selected_category_ids ?? [] }
    }

    if (values.content_block_type === 'product_grid') {
      variables.config = {
        mode: values.product_grid_mode || 'newest',
        max_products: values.product_grid_max_products || 8,
        tag_ids: values.product_grid_tag_ids ?? [],
      }
      // `grid_products` (ordered product container ids) is sent in manual
      // mode ALWAYS, and in every other mode only when CREATING (`!id`). On
      // UPDATE it's omitted outside manual mode so switching modes (or
      // saving other fields while in 'newest' mode) never wipes the manual
      // picks — omitted there, which the resolver reads as "leave the grid
      // untouched". CREATE has no existing table to protect, so it always
      // sends whatever picks the form carries — otherwise duplicating a
      // dormant 'newest'-mode block would silently drop the picks it copied
      // from its source (see `buildDuplicateFormValues` in
      // `contentBlockPayload.ts`, which this form mirrors).
      if (!id || (values.product_grid_mode || 'newest') === 'manual') {
        const seen = new Set<number>()
        const ids: number[] = []
        for (const pick of values.grid_product_picks ?? []) {
          if (!seen.has(pick.id)) {
            seen.add(pick.id)
            ids.push(pick.id)
          }
        }
        variables.grid_products = ids
      }
    }

    if (values.content_block_type === 'page_grid') {
      variables.config = {
        mode: values.page_grid_mode || 'manual',
        max_pages: values.page_grid_max_pages || 8,
        page_ids:
          values.page_grid_mode === 'manual' ? values.selected_page_ids : [],
        parent_id:
          values.page_grid_mode === 'children'
            ? values.page_grid_parent_id || null
            : null,
        show_tags: values.page_grid_show_tags ?? false,
        items_per_row: values.page_grid_items_per_row || 4,
      }
    }

    if (values.content_block_type === 'email_signup') {
      variables.config = {
        delivery: values.email_signup_delivery || 'email',
        notify_email:
          values.email_signup_delivery === 'mailerlite'
            ? null
            : values.email_signup_notify_email || null,
      }
    }

    // Block-level title settings live in `config`. Always send a MERGED config:
    // the loaded config FIRST, then whatever grid/category keys the branches
    // above built, then the title keys — so no stored key (mode, page_ids,
    // parent_id, show_tags, category_ids, …) is dropped by the custom mutations.
    // Buttons render the name as a label (no title) and their mutations don't
    // carry `$config`, so skip them.
    if (!isButtonType) {
      const builtConfig =
        (variables.config as Record<string, unknown> | null) ?? {}
      variables.config = {
        ...rawConfigRef.current,
        ...builtConfig,
        titleAlignment:
          values.titleAlignment ?? naturalTitleAlign(values.style),
        hideTitle: values.hideTitle ?? false,
        display: values.textOnImageDisplay ?? 'full_width',
        mediaRatio: values.mediaRatio ?? '50_50',
        video_controls: values.video_controls ?? true,
      }
    }

    if (id) {
      const mutate = isButtonType
        ? updateButtonContentBlock
        : isLinkedType
          ? updateLinkedContentBlock
          : updateContentBlock
      mutate({
        variables: {
          where: { id },
          ...variables,
        },
      })
    } else {
      const mutate = isButtonType
        ? createButtonContentBlock
        : isLinkedType
          ? createLinkedContentBlock
          : createContentBlock
      mutate({
        variables,
      })
    }
  }

  useEffect(() => {
    if (!loadingData) {
      if (contentBlockData) {
        // Keep the raw loaded config so onSubmit can spread it first.
        rawConfigRef.current = contentBlockData.config ?? {}
        const existingProfiles =
          contentBlockData.content_block_profiles?.filter(
            (p: { language: string }) =>
              p.language && allLanguages.includes(p.language)
          ) ?? []

        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            (p: { language: string }) => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              description_format:
                existingProfile.description_format ?? 'plain',
              link: existingProfile.link ?? '',
              cta_label: existingProfile.cta_label ?? '',
              cta_link: existingProfile.cta_link ?? '',
              language: existingProfile.language,
            }
          } else {
            return {
              name: '',
              description: '',
              description_format: 'plain' as const,
              link: '',
              cta_label: '',
              cta_link: '',
              language: lang,
            }
          }
        })

        form.reset({
          content_block_profiles: allProfiles,
          content_block_type: contentBlockData.type || 'only_text',
          style: contentBlockData.style || undefined,
          images: contentBlockData.main_image
            ? [contentBlockData.main_image.src]
            : [],
          gallery_images:
            contentBlockData.gallery_images?.map(
              (img: { src: string }) => img.src
            ) ?? [],
          grid_product_picks: contentBlockData.grid_product_containers || [],
          video_link: contentBlockData.video_link || '',
          video_type: contentBlockData.video_type || undefined,
          video_autoplay: false,
          video_loop: false,
          default_margins: true,
          object_fit_cover: false,
          extra_css: contentBlockData.extra_css
            ? JSON.stringify(contentBlockData.extra_css, null, 2)
            : '',
          text_color: contentBlockData.text_color || '',
          background_color: contentBlockData.background_color || '',
          link_new_tab: contentBlockData.link_new_tab || false,
          link_page_id: contentBlockData.link_page?.id || '',
          cta_new_tab: contentBlockData.cta_new_tab || false,
          cta_link_page_id:
            contentBlockData.cta_link_page_id ||
            contentBlockData.cta_link_page?.id ||
            '',
          cta_link_type: contentBlockData.cta_link_type ?? null,
          cta_link_category_id: contentBlockData.cta_link_category?.id ?? null,
          linked_items:
            contentBlockData.linked_items?.map((item: any) => ({
              id: item.id,
              title: item.title,
              link: item.link,
              arrangement: item.arrangement,
              image: item.image?.src || '',
              imageId: item.image?.id || null,
            })) ?? [],
          selected_category_ids: contentBlockData.config?.category_ids ?? [],
          // `config.mode` is shared by both grid types but its allowed values
          // differ ('newest'|'manual' vs 'manual'|'children'), so only read it
          // into the field whose block type matches — otherwise a page_grid in
          // 'children' mode sets an invalid product_grid_mode and blocks submit.
          product_grid_mode:
            contentBlockData.type === 'product_grid'
              ? (contentBlockData.config as any)?.mode || 'newest'
              : 'newest',
          product_grid_max_products: (contentBlockData.config as any)?.max_products || 8,
          product_grid_tag_ids:
            contentBlockData.type === 'product_grid'
              ? ((contentBlockData.config as any)?.tag_ids ?? [])
              : [],
          page_grid_mode:
            contentBlockData.type === 'page_grid'
              ? (contentBlockData.config as any)?.mode || 'manual'
              : 'manual',
          page_grid_max_pages: (contentBlockData.config as any)?.max_pages || 8,
          page_grid_items_per_row:
            (contentBlockData.config as any)?.items_per_row || 4,
          selected_page_ids: (contentBlockData.config as any)?.page_ids ?? [],
          page_grid_parent_id: (contentBlockData.config as any)?.parent_id || '',
          page_grid_show_tags: (contentBlockData.config as any)?.show_tags ?? false,
          email_signup_delivery:
            (contentBlockData.config as any)?.delivery || 'email',
          email_signup_notify_email:
            (contentBlockData.config as any)?.notify_email || '',
          titleAlignment:
            contentBlockData.config?.titleAlignment ??
            naturalTitleAlign(contentBlockData.style),
          hideTitle: Boolean(contentBlockData.config?.hideTitle),
          textOnImageDisplay:
            (contentBlockData.config as any)?.display ?? 'full_width',
          mediaRatio: (contentBlockData.config as any)?.mediaRatio ?? '50_50',
          video_controls:
            (contentBlockData.config as any)?.video_controls ?? true,
        })
      } else {
        rawConfigRef.current = {}
        form.reset({
          content_block_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            description_format: 'plain' as const,
            link: '',
            cta_label: '',
            cta_link: '',
            language: lang,
          })),
          content_block_type: 'only_text',
          style: undefined,
          images: [],
          gallery_images: [],
          grid_product_picks: [],
          video_link: '',
          video_type: undefined,
          video_autoplay: false,
          video_loop: false,
          default_margins: true,
          object_fit_cover: false,
          extra_css: '',
          text_color: '',
          background_color: '',
          link_new_tab: false,
          link_page_id: '',
          cta_new_tab: false,
          cta_link_page_id: '',
          cta_link_type: null,
          cta_link_category_id: null,
          linked_items: [],
          selected_category_ids: [],
          product_grid_mode: 'newest',
          product_grid_max_products: 8,
          product_grid_tag_ids: [],
          page_grid_mode: 'manual',
          page_grid_max_pages: 8,
          page_grid_items_per_row: 4,
          selected_page_ids: [],
          page_grid_show_tags: false,
          email_signup_delivery: 'email',
          email_signup_notify_email: '',
          titleAlignment: naturalTitleAlign(undefined),
          hideTitle: false,
          textOnImageDisplay: 'full_width',
          mediaRatio: '50_50',
          video_controls: true,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentBlockData, loadingData, allLanguages])

  // Create mode only: keep `titleAlignment` tracking the selected style's
  // natural default until the user manually overrides the control (once the
  // field is dirty we leave it alone). Edit mode seeds from stored config/style
  // above and must not be re-derived here.
  useEffect(() => {
    if (id) return
    if (form.formState.dirtyFields.titleAlignment) return
    form.setValue('titleAlignment', naturalTitleAlign(selectedStyle))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStyle, id])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Content Block</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Type"
                description="Select the content block type"
                control={form.control}
                name="content_block_type"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {contentBlockTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {showStyleDropdown && (
                <InputFieldContainer
                  label="Style"
                  description="Visual layout style for this content block"
                  control={form.control}
                  name="style"
                  render={({ field }) => {
                    const styleOptions = isButton
                      ? buttonStyles
                      : isGallery
                        ? galleryStyles
                        : isTextAndImage
                          ? textAndImageStyles
                          : textStyles
                    return (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <StyleSelector
                            options={styleOptions}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />
              )}

              <InputFieldContainer
                label="Title"
                description="Title for this content block"
                control={form.control}
                name={`content_block_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <TitleAlignmentControl form={form} />

              {showDescription && (
                <>
                  <InputFieldContainer
                    label="Description format"
                    control={form.control}
                    name={`content_block_profiles.${languageId}.description_format`}
                    render={({
                      field,
                    }: {
                      field: {
                        value: 'plain' | 'markdown'
                        onChange: (value: 'plain' | 'markdown') => void
                      }
                    }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={(field.value ?? 'plain') === 'plain'}
                                onChange={() => field.onChange('plain')}
                              />
                              <span className="text-sm">Plain</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={field.value === 'markdown'}
                                onChange={() => field.onChange('markdown')}
                              />
                              <span className="text-sm">Markdown</span>
                            </label>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label="Description"
                    control={form.control}
                    name={`content_block_profiles.${languageId}.description`}
                    render={({ field }: { field: any }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <RichTextField
                            field={field}
                            format={
                              form.watch(
                                `content_block_profiles.${languageId}.description_format`
                              ) ?? 'plain'
                            }
                            rows={6}
                            placeholder="Description"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              {isTextAndImage &&
                (selectedStyle === 'text_on_image' ||
                  selectedStyle === 'side_by_side' ||
                  selectedStyle === 'image_left') && (
                  <CtaEditor
                    control={form.control}
                    languageId={languageId}
                    labelName={`content_block_profiles.${languageId}.cta_label`}
                    linkName={`content_block_profiles.${languageId}.cta_link`}
                    newTabName="cta_new_tab"
                    linkPageIdName="cta_link_page_id"
                    linkTypeName="cta_link_type"
                    linkCategoryIdName="cta_link_category_id"
                  />
                )}

              {isTextAndImage && selectedStyle === 'text_on_image' && (
                <InputFieldContainer
                  label="Display"
                  description="Whether the block spans the full page width or is constrained to the content width"
                  control={form.control}
                  name="textOnImageDisplay"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || 'full_width'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select display" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="full_width">Full width</SelectItem>
                          <SelectItem value="content_width">Content width</SelectItem>
                          <SelectItem value="text_width">Text width</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Image / Text width ratio (Side by Side & Image Left styles only) */}
              {isTextAndImage &&
                (selectedStyle === 'side_by_side' ||
                  selectedStyle === 'image_left') && (
                  <InputFieldContainer
                    label="Image / Text ratio"
                    description="How the image and text columns split the row (image : text)"
                    control={form.control}
                    name="mediaRatio"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || '50_50'}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select ratio" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="50_50">
                              Image 50% / Text 50%
                            </SelectItem>
                            <SelectItem value="60_40">
                              Image 60% / Text 40%
                            </SelectItem>
                            <SelectItem value="40_60">
                              Image 40% / Text 60%
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

              {showLinkConfig && (
                <>
                  <InputFieldContainer
                    label="Link Type"
                    control={form.control}
                    name="link_page_id"
                    render={({
                      field,
                    }: {
                      field: {
                        value: string
                        onChange: (value: string) => void
                      }
                    }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <div className="flex gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={!field.value}
                                onChange={() => field.onChange('')}
                              />
                              <span className="text-sm">External URL</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                checked={!!field.value}
                                onChange={() => field.onChange('select')}
                              />
                              <span className="text-sm">Internal Page</span>
                            </label>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!form.watch('link_page_id') ? (
                    <>
                      <InputFieldContainer
                        label="URL"
                        control={form.control}
                        name={`content_block_profiles.${languageId}.link`}
                        render={({
                          field,
                        }: {
                          field: React.InputHTMLAttributes<HTMLInputElement>
                        }) => (
                          <FormItem className="col-span-3">
                            <FormControl>
                              <Input
                                placeholder="https://example.com"
                                type="text"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <InputFieldContainer
                        label="New Tab"
                        control={form.control}
                        name="link_new_tab"
                        render={({
                          field,
                        }: {
                          field: {
                            value: boolean
                            onChange: (value: boolean) => void
                          }
                        }) => (
                          <FormItem className="col-span-3">
                            <FormControl>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                                <Label className="text-sm font-normal">
                                  Open in new tab
                                </Label>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  ) : (
                    <InputFieldContainer
                      label="Page"
                      control={form.control}
                      name="link_page_id"
                      render={({
                        field,
                      }: {
                        field: {
                          value: string
                          onChange: (value: string) => void
                        }
                      }) => (
                        <FormItem className="col-span-3">
                          <FormControl>
                            <PageSelector
                              value={field.value}
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </>
              )}

              {showImageUpload && !isGallery && (
                <InputFieldContainer
                  label="Image"
                  description="Upload content block image"
                  control={form.control}
                  name="images"
                  render={field => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <ImageUpload maxImages={1} variant="large" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isGallery && (
                <InputFieldContainer
                  label="Gallery Images"
                  description="Upload multiple images for the gallery"
                  control={form.control}
                  name="gallery_images"
                  render={field => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <ImageUpload
                          maxImages={10}
                          variant="large"
                          enableReordering
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isGallery && (
                <InputFieldContainer
                  label="Display"
                  description="Whether the block spans the full page width, the content width, or the narrower text width"
                  control={form.control}
                  name="textOnImageDisplay"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || 'full_width'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select display" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="full_width">
                            Full width
                          </SelectItem>
                          <SelectItem value="content_width">
                            Content width
                          </SelectItem>
                          <SelectItem value="text_width">
                            Text width
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isPhotoLinks && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Linked Items</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const current = form.getValues('linked_items') || []
                        if (current.length < 6) {
                          form.setValue('linked_items', [
                            ...current,
                            {
                              title: '',
                              link: '',
                              arrangement: current.length,
                              image: '',
                              imageId: null,
                            },
                          ])
                        }
                      }}
                    >
                      + Add Item
                    </Button>
                  </div>
                  {(form.watch('linked_items') || []).map((item, index) => (
                    <div
                      key={index}
                      className="border rounded-md p-4 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          Item {index + 1}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const current = form.getValues('linked_items') || []
                            form.setValue(
                              'linked_items',
                              current.filter((_, i) => i !== index)
                            )
                          }}
                        >
                          <Icons.trash className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      <Input
                        placeholder="Title"
                        value={item.title}
                        onChange={e => {
                          const current = form.getValues('linked_items') || []
                          current[index] = {
                            ...current[index],
                            title: e.target.value,
                          }
                          form.setValue('linked_items', [...current])
                        }}
                      />
                      <Input
                        placeholder="Link (e.g., /en/categories/sofas)"
                        value={item.link}
                        onChange={e => {
                          const current = form.getValues('linked_items') || []
                          current[index] = {
                            ...current[index],
                            link: e.target.value,
                          }
                          form.setValue('linked_items', [...current])
                        }}
                      />
                      {/* Image upload */}
                      <ImageUploadV2
                        field={
                          {
                            name: `linked_items.${index}.imageId`,
                            value: item.imageId,
                            onChange: (newImageId: number | null) => {
                              const current =
                                form.getValues('linked_items') || []
                              current[index] = {
                                ...current[index],
                                imageId: newImageId,
                                image: '',
                              }
                              form.setValue('linked_items', [...current])
                            },
                            onBlur: () => {},
                            ref: () => {},
                          } as any
                        }
                        imageName={`content-block-linked-item-${index}`}
                        existingImageUrl={item.image || undefined}
                      />
                      {/* Fallback URL input when no upload */}
                      {!item.imageId && (
                        <Input
                          placeholder="Or paste image URL"
                          value={item.image || ''}
                          onChange={e => {
                            const current = form.getValues('linked_items') || []
                            current[index] = {
                              ...current[index],
                              image: e.target.value,
                              imageId: null,
                            }
                            form.setValue('linked_items', [...current])
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {isCategoryTiles && (
                <div className="space-y-4">
                  {/* Selected Categories */}
                  <div className="overflow-hidden">
                    <Label className="text-sm font-medium">
                      Selected Categories
                    </Label>
                    {selectedCategoryIds.length === 0 ? (
                      <p className="text-sm text-muted-foreground mt-2">
                        No categories selected
                      </p>
                    ) : (
                      <DragDropContext onDragEnd={handleCategoryDragEnd}>
                        <Droppable
                          droppableId="selected-categories"
                          direction="horizontal"
                        >
                          {provided => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="mt-3 flex flex-wrap gap-3 pb-2"
                            >
                              {selectedCategoryIds.map((catId, index) => {
                                const cat = allCategories.find(
                                  (c: any) => c.id === catId
                                )
                                const name = cat
                                  ? getCategoryName(cat.category_profiles)
                                  : `Category #${catId}`
                                const bannerSrc =
                                  cat?.banners?.[0]?.src || cat?.image?.src
                                return (
                                  <Draggable
                                    key={catId}
                                    draggableId={String(catId)}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`relative flex-shrink-0 w-32 rounded-lg border overflow-hidden group ${
                                          snapshot.isDragging
                                            ? 'shadow-lg ring-2 ring-primary'
                                            : ''
                                        }`}
                                      >
                                        {/* Drag handle */}
                                        <div
                                          {...provided.dragHandleProps}
                                          className="absolute top-1 left-1 z-10 bg-background/80 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab"
                                        >
                                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        {/* Remove button */}
                                        <Button
                                          type="button"
                                          variant="destructive"
                                          size="icon"
                                          className="absolute top-1 right-1 z-10 h-6 w-6 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                          onClick={() =>
                                            handleRemoveCategory(catId)
                                          }
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                        {/* Banner image */}
                                        <div className="aspect-[3/4] bg-muted">
                                          {bannerSrc ? (
                                            <img
                                              src={bannerSrc}
                                              alt={name}
                                              className="w-full h-full object-cover"
                                            />
                                          ) : (
                                            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                              No image
                                            </div>
                                          )}
                                        </div>
                                        {/* Category name */}
                                        <div className="px-2 py-2 text-sm font-medium text-center truncate border-t">
                                          {name}
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                )
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    )}
                  </div>

                  {/* Available Categories */}
                  <div>
                    <Label className="text-sm font-medium">
                      Available Categories
                    </Label>
                    {loadingCategories ? (
                      <p className="text-sm text-muted-foreground mt-2">
                        Loading categories...
                      </p>
                    ) : unassignedCategories.length === 0 ? (
                      <p className="text-sm text-muted-foreground mt-2">
                        All categories selected
                      </p>
                    ) : (
                      <div className="mt-2 flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                        {unassignedCategories.map((cat: any) => {
                          const bannerSrc =
                            cat?.banners?.[0]?.src || cat?.image?.src
                          return (
                            <div
                              key={cat.id}
                              className="flex-shrink-0 w-28 rounded-lg border overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                              onClick={() => handleAddCategory(cat.id)}
                            >
                              <div className="aspect-[3/4] bg-muted">
                                {bannerSrc ? (
                                  <img
                                    src={bannerSrc}
                                    alt={getCategoryName(cat.category_profiles)}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                    No image
                                  </div>
                                )}
                              </div>
                              <div className="px-1.5 py-1.5 text-xs text-center truncate border-t flex items-center justify-between">
                                <span className="truncate">
                                  {getCategoryName(cat.category_profiles)}
                                </span>
                                <Plus className="h-3 w-3 flex-shrink-0 ml-1" />
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {isEmailSignup && (
                <>
                  <InputFieldContainer
                    label="Delivery"
                    control={form.control}
                    name="email_signup_delivery"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || 'email'}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select delivery" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="mailerlite">MailerLite</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {showRecipientEmail && (
                    <InputFieldContainer
                      label="Recipient Email"
                      description="Leave empty to use Partner Request Email"
                      control={form.control}
                      name="email_signup_notify_email"
                      render={({ field }) => (
                        <FormItem className="col-span-3">
                          <FormControl>
                            <Input
                              placeholder="name@example.com"
                              type="email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </>
              )}

              {isProductGrid && (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Mode</label>
                    <Controller
                      name="product_grid_mode"
                      control={form.control}
                      render={({ field }) => (
                        <Select
                          value={field.value || 'newest'}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="newest">Newest Products</SelectItem>
                            <SelectItem value="manual">Manual Selection</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">
                      Max Products
                    </label>
                    <Controller
                      name="product_grid_max_products"
                      control={form.control}
                      render={({ field }) => (
                        <Input
                          type="number"
                          min={1}
                          max={8}
                          value={field.value || 8}
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value) || 8)
                          }
                        />
                      )}
                    />
                  </div>
                  {productGridMode !== 'manual' && (
                    <div>
                      <label className="text-sm font-medium">
                        Filter by Tags
                      </label>
                      <Controller
                        name="product_grid_tag_ids"
                        control={form.control}
                        render={({ field }) => (
                          <TagsSelect field={field} />
                        )}
                      />
                    </div>
                  )}
                  {productGridMode === 'manual' && (
                    <div>
                      <label className="text-sm font-medium">
                        Select Products
                      </label>
                      <Controller
                        name="grid_product_picks"
                        control={form.control}
                        render={({ field }) => (
                          <GridProductPicker field={field} />
                        )}
                      />
                    </div>
                  )}
                </div>
              )}

              {isPageGrid && (
                <div className="space-y-4">
                  <InputFieldContainer
                    label="Mode"
                    control={form.control}
                    name="page_grid_mode"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <Select
                          value={field.value || 'manual'}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select mode" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="manual">
                              Manual Selection
                            </SelectItem>
                            <SelectItem value="children">Child Pages</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <InputFieldContainer
                    label="Max Pages"
                    control={form.control}
                    name="page_grid_max_pages"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={24}
                            value={field.value || 8}
                            onChange={e =>
                              field.onChange(parseInt(e.target.value) || 8)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <InputFieldContainer
                    label="Items Per Row"
                    control={form.control}
                    name="page_grid_items_per_row"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            type="number"
                            min={2}
                            max={6}
                            value={field.value || 4}
                            onChange={e =>
                              field.onChange(parseInt(e.target.value) || 4)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <InputFieldContainer
                    label="Show Tags"
                    control={form.control}
                    name="page_grid_show_tags"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <p className="text-sm text-muted-foreground">
                    Child Pages mode lists the children of a page — by default the page
                    this block is attached to, or the Parent Page you choose below.
                  </p>
                  {pageGridMode === 'children' && (
                    <>
                      <InputFieldContainer
                        label="Parent Page"
                        control={form.control}
                        name="page_grid_parent_id"
                        render={({ field }) => (
                          <FormItem className="col-span-3">
                            <Select
                              value={field.value ? field.value : '__current__'}
                              onValueChange={value =>
                                field.onChange(value === '__current__' ? '' : value)
                              }
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Current page (default)" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="__current__">
                                  Current page (default)
                                </SelectItem>
                                {allPages.map((page: any) => (
                                  <SelectItem key={page.id} value={page.id}>
                                    {getPageTitle(page)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <p className="text-sm text-muted-foreground">
                        Defaults to this page's children. Pick a page to show that
                        page's children instead — useful on the homepage, where there
                        is no current page.
                      </p>
                    </>
                  )}
                  {pageGridMode === 'manual' && (
                    <>
                      {/* Selected Pages */}
                      <div className="overflow-hidden">
                        <Label className="text-sm font-medium">
                          Selected Pages
                        </Label>
                        {selectedPageIds.length === 0 ? (
                          <p className="text-sm text-muted-foreground mt-2">
                            No pages selected
                          </p>
                        ) : (
                          <DragDropContext onDragEnd={handlePageDragEnd}>
                            <Droppable
                              droppableId="selected-pages"
                              direction="horizontal"
                            >
                              {provided => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  className="mt-3 flex flex-wrap gap-3 pb-2"
                                >
                                  {selectedPageIds.map((pageId, index) => {
                                    const page = allPages.find(
                                      (p: any) => p.id === pageId
                                    )
                                    const title = page
                                      ? getPageTitle(page)
                                      : pageId
                                    const imageSrc = page?.hero_image?.src
                                    return (
                                      <Draggable
                                        key={pageId}
                                        draggableId={pageId}
                                        index={index}
                                      >
                                        {(provided, snapshot) => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            className={`relative flex-shrink-0 w-32 rounded-lg border overflow-hidden group ${
                                              snapshot.isDragging
                                                ? 'shadow-lg ring-2 ring-primary'
                                                : ''
                                            }`}
                                          >
                                            {/* Drag handle */}
                                            <div
                                              {...provided.dragHandleProps}
                                              className="absolute top-1 left-1 z-10 bg-background/80 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab"
                                            >
                                              <GripVertical className="h-4 w-4 text-muted-foreground" />
                                            </div>
                                            {/* Remove button */}
                                            <Button
                                              type="button"
                                              variant="destructive"
                                              size="icon"
                                              className="absolute top-1 right-1 z-10 h-6 w-6 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                              onClick={() =>
                                                handleRemovePage(pageId)
                                              }
                                            >
                                              <X className="h-3 w-3" />
                                            </Button>
                                            {/* Hero image */}
                                            <div className="aspect-[3/4] bg-muted">
                                              {imageSrc ? (
                                                <img
                                                  src={imageSrc}
                                                  alt={title}
                                                  className="w-full h-full object-cover"
                                                />
                                              ) : (
                                                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                                  No image
                                                </div>
                                              )}
                                            </div>
                                            {/* Page title */}
                                            <div className="px-2 py-2 text-sm font-medium text-center truncate border-t">
                                              {title}
                                            </div>
                                          </div>
                                        )}
                                      </Draggable>
                                    )
                                  })}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        )}
                      </div>

                      {/* Available Pages */}
                      <div>
                        <Label className="text-sm font-medium">
                          Available Pages
                        </Label>
                        {loadingPages ? (
                          <p className="text-sm text-muted-foreground mt-2">
                            Loading pages...
                          </p>
                        ) : unassignedPages.length === 0 ? (
                          <p className="text-sm text-muted-foreground mt-2">
                            All pages selected
                          </p>
                        ) : (
                          <div className="mt-2 flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                            {unassignedPages.map((page: any) => {
                              const imageSrc = page?.hero_image?.src
                              return (
                                <div
                                  key={page.id}
                                  className="flex-shrink-0 w-28 rounded-lg border overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                                  onClick={() => handleAddPage(page.id)}
                                >
                                  <div className="aspect-[3/4] bg-muted">
                                    {imageSrc ? (
                                      <img
                                        src={imageSrc}
                                        alt={getPageTitle(page)}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                        No image
                                      </div>
                                    )}
                                  </div>
                                  <div className="px-1.5 py-1.5 text-xs text-center truncate border-t flex items-center justify-between">
                                    <span className="truncate">
                                      {getPageTitle(page)}
                                    </span>
                                    <Plus className="h-3 w-3 flex-shrink-0 ml-1" />
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {showVideoInput && (
                <>
                  <InputFieldContainer
                    label="Video Type"
                    description="Select the video source type"
                    control={form.control}
                    name="video_type"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select video type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {videoTypeOptions.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label="Display"
                    description="Whether the block spans the full page width, the content width, or the narrower text width"
                    control={form.control}
                    name="textOnImageDisplay"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || 'full_width'}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select display" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="full_width">
                              Full width
                            </SelectItem>
                            <SelectItem value="content_width">
                              Content width
                            </SelectItem>
                            <SelectItem value="text_width">
                              Text width
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label={
                      selectedVideoType === 'uploaded' ? 'Video' : 'Video Link'
                    }
                    description={
                      selectedVideoType === 'uploaded'
                        ? 'Upload a video file (MP4 or WebM)'
                        : 'Enter the video URL (YouTube or Vimeo link)'
                    }
                    control={form.control}
                    name="video_link"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          {selectedVideoType === 'uploaded' ? (
                            <VideoUploadV2 field={field} />
                          ) : (
                            <Input
                              placeholder="https://www.youtube.com/watch?v=..."
                              type="text"
                              {...field}
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {selectedVideoType === 'uploaded' && (
                    <InputFieldContainer
                      label="Controls"
                      control={form.control}
                      name="video_controls"
                      render={({
                        field,
                      }: {
                        field: {
                          value: boolean
                          onChange: (value: boolean) => void
                        }
                      }) => (
                        <FormItem className="col-span-3">
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                              <Label className="text-sm font-normal">
                                Show video controls
                              </Label>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </>
              )}

              <InputFieldContainer
                label="Custom Styles"
                description="Additional CSS styling as JSON"
                control={form.control}
                name="extra_css"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <StyleEditor
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2">
                {id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDelete}
                    className="shrink-0"
                  >
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <Button type="submit" disabled={loadingData} className="flex-1">
                  {id ? 'Update' : 'Create'}
                </Button>
              </div>

              {id && (
                <ContentBlockUsages key={usagesRefreshKey} id={id} />
              )}
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
